import process from 'process';
import { Buffer } from 'buffer';
import { useEffect } from 'react';
import RouterProvider from '@/routes/index';
import { RemoteConfigProvider } from './hooks';
import { onError } from '@apollo/client/link/error';
import { LoaderProvider } from './hooks/LoaderContext';
import ErrorBoundary from './components/ErrorBoundary';
import { setContext } from '@apollo/client/link/context';
import { setWasmUrl } from '@lottiefiles/dotlottie-react';
import { forceSessionExpiredLogout, getOnStorage, isUnauthorizedGraphQLError } from './shared';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ModalTimeChange from './components/modal-time-change/ModalTimeChange';
import { from, HttpLink, ApolloClient, InMemoryCache, ApolloProvider } from '@apollo/client';

import { clarity } from 'react-microsoft-clarity';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  },
});

import {
  AlertProvider,
  UserProvider,
  FetchProvider,
  TablesProvider,
  GlobalProvider,
  ClientProvider,
  TimeMonitorProvider,
  ClientHierarchyProvider,
  useNetworkStatusContext,
} from '@/hooks';

window.Buffer = Buffer;
window.process = process;

const log = console.log;

console.log = (val: any, ...params: any[]) => {
  if (!window.location.href.includes('pwa-rca-prod.bateforte')) {
    log(val, ...params);
  }
};

const App = () => {
  const { updateConnectivityStatus } = useNetworkStatusContext();

  const isProd = window.location.hostname.includes('pwa-rca-prod');

  useEffect(() => {
    if (isProd) {
      clarity.init('mvfp9i5cf0');
    }

    const codPessoa = getOnStorage('cod_pessoa');
    const rcaCustomer = getOnStorage('rca_usuario') as
      | { tipoUsuario?: string; tipo_usuario?: string }
      | undefined;

    if (codPessoa && window.clarity) {
      window.clarity(
        'identify',
        String(codPessoa),
        undefined,
        undefined,
        String(codPessoa)
      );

      const tipoUsuario = rcaCustomer?.tipoUsuario || rcaCustomer?.tipo_usuario;
      if (tipoUsuario) {
        window.clarity('set', 'perfil_usuario', String(tipoUsuario));
      }
    }
  }, [isProd]);

  const httpLink = new HttpLink({
    uri: window.agp_config.MAGENTO_URL,
  });

  useEffect(() => {
    setWasmUrl(`${process.env.PUBLIC_URL}/dotlottie-player.wasm`);
  }, []);

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration?.active?.postMessage({
        type: 'CONFIG',
        config: window.agp_config,
      });
    });
  }

  const authLink = setContext(async (operation, { headers }) => {
    const token = getOnStorage('token_magento');

    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json; charset=utf-8',
      },
    };
  });

  const errorHandlerLink = onError(({ networkError, graphQLErrors }) => {
    if (networkError) {
      updateConnectivityStatus('offline');
      return;
    }

    if (isUnauthorizedGraphQLError(graphQLErrors)) {
      void forceSessionExpiredLogout();
    }
  });

  const shouldReload = window.localStorage.getItem('reload');
  if (shouldReload) {
    window.localStorage.removeItem('reload');
    window.location.reload();
  }

  const client = new ApolloClient({
    cache: new InMemoryCache(),
    link: from([authLink, errorHandlerLink, httpLink]),
  });

  return (
    <ApolloProvider client={client}>
      <QueryClientProvider client={queryClient}>
        <FetchProvider>
          <RemoteConfigProvider>
            <TablesProvider>
              <ErrorBoundary>
                <LoaderProvider>
                  <ClientProvider>
                    <UserProvider>
                      <GlobalProvider>
                        <TimeMonitorProvider>
                          <ClientHierarchyProvider>
                            <AlertProvider>
                              <ModalTimeChange />
                              <RouterProvider />
                            </AlertProvider>
                          </ClientHierarchyProvider>
                        </TimeMonitorProvider>
                      </GlobalProvider>
                    </UserProvider>
                  </ClientProvider>
                </LoaderProvider>
              </ErrorBoundary>
            </TablesProvider>
          </RemoteConfigProvider>
        </FetchProvider>
      </QueryClientProvider>
    </ApolloProvider>
  );
};

export default App;
